// Mock product screen card -- lifted out of the marketing homepage so
// per-trade landing pages can show a trade-flavoured version. Renders
// at lg+ only; mobile / tablet the hero stays single-column.
//
// The visual is a small "what the app looks like" card: a header row
// with a tinted icon + title + subtitle, then 3-5 list rows each with
// a coloured stage pip and a stage label. The decorative blur sits
// behind the card at -z-10.
//
// Per-trade callers pick:
//   - title (e.g. "Inbox", "Today's schedule", "Quotes drafted")
//   - subtitle (e.g. "3 items need attention")
//   - icon -- one of the supported Phosphor icons via `iconKey`
//   - items -- 3-5 rows with realistic-sounding client + job names
//   - accent -- amber (default), or another tailwind hue family
import * as React from "react";
import {
  Lightning,
  Tray,
  CalendarBlank,
  Receipt,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react";

export type MockAppScreenAccent =
  | "amber"
  | "blue"
  | "green"
  | "rose"
  | "violet"
  | "teal"
  | "orange";

export type MockAppScreenIconKey = "lightning" | "inbox" | "calendar" | "receipt";

export interface MockAppScreenItem {
  /** Top-line text on the row -- typically `Customer name -- Job title`. */
  title: string;
  /** Stage label in the small chip (e.g. "Quoted", "Scheduled", "Paid"). */
  stage: string;
  /** Hex colour for the stage pip + chip. */
  color: string;
}

export interface MockAppScreenProps {
  title: string;
  subtitle: string;
  iconKey?: MockAppScreenIconKey;
  items: MockAppScreenItem[];
  accent?: MockAppScreenAccent;
  className?: string;
}

const ICONS: Record<MockAppScreenIconKey, PhosphorIcon> = {
  lightning: Lightning,
  inbox: Tray,
  calendar: CalendarBlank,
  receipt: Receipt,
};

// Header chip + decorative blur take their hue from the accent. Card
// chrome stays neutral so the mock screen reads as a real product
// surface, not a marketing slide.
const ACCENT_HEADER_BG: Record<MockAppScreenAccent, string> = {
  amber: "bg-amber-100",
  blue: "bg-blue-100",
  green: "bg-green-100",
  rose: "bg-rose-100",
  violet: "bg-violet-100",
  teal: "bg-teal-100",
  orange: "bg-orange-100",
};

const ACCENT_HEADER_FG: Record<MockAppScreenAccent, string> = {
  amber: "text-amber-600",
  blue: "text-blue-600",
  green: "text-green-600",
  rose: "text-rose-600",
  violet: "text-violet-600",
  teal: "text-teal-600",
  orange: "text-orange-600",
};

const ACCENT_BLUR: Record<MockAppScreenAccent, string> = {
  amber: "bg-amber-200/30",
  blue: "bg-blue-200/30",
  green: "bg-green-200/30",
  rose: "bg-rose-200/30",
  violet: "bg-violet-200/30",
  teal: "bg-teal-200/30",
  orange: "bg-orange-200/30",
};

const ACCENT_SHADOW: Record<MockAppScreenAccent, string> = {
  amber: "shadow-amber-500/5",
  blue: "shadow-blue-500/5",
  green: "shadow-green-500/5",
  rose: "shadow-rose-500/5",
  violet: "shadow-violet-500/5",
  teal: "shadow-teal-500/5",
  orange: "shadow-orange-500/5",
};

export function MockAppScreen({
  title,
  subtitle,
  iconKey = "lightning",
  items,
  accent = "amber",
  className,
}: MockAppScreenProps) {
  const Icon = ICONS[iconKey];
  return (
    <div className={`relative ${className ?? ""}`}>
      <div
        className={`rounded-lg border bg-card shadow-md ${ACCENT_SHADOW[accent]} p-6 space-y-4`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b">
          <div
            className={`h-8 w-8 rounded-lg flex items-center justify-center ${ACCENT_HEADER_BG[accent]}`}
          >
            <Icon className={`h-4 w-4 ${ACCENT_HEADER_FG[accent]}`} weight="fill" />
          </div>
          <div>
            <p className="text-sm font-semibold">{title}</p>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        {/* Item rows */}
        {items.map((item) => (
          <div
            key={item.title}
            className="flex items-center gap-3 p-3 rounded-md border"
          >
            <div
              className="w-1 h-10 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <div className="flex-1">
              <p className="text-sm font-medium">{item.title}</p>
              <span
                className="text-[10px] px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: item.color + "20",
                  color: item.color,
                }}
              >
                {item.stage}
              </span>
            </div>
          </div>
        ))}
      </div>
      {/* Decorative blur behind the card */}
      <div
        className={`absolute -z-10 -top-8 -right-8 w-64 h-64 rounded-full blur-3xl ${ACCENT_BLUR[accent]}`}
      />
    </div>
  );
}
